import React from 'react';
import PropTypes from 'prop-types';

import 'whatwg-fetch';

import TraitSelector from '../../shared/TraitSelector';
import QuantityInput from '../../shared/QuantityInput';
import ProductPriceAndInventory from '../../shared/ProductPriceAndInventory';
import miniCartClient from '../../../util/miniCartClient';
import AddToWishlistApp from '../../../addtowishlist/AddToWishlistApp';
import { addResourceStrings, translateResourceString } from '../../../util/translationUtility'

import { MessagesList, ERROR_USER_ALERT, INFO_USER_ALERT } from '../../../SharedComponents/UserMessaging';
import { makeCatalystFetchPostInit } from '../../../util/fetchUtility';

let messageId = 0;
const createUserMessage = ({ text, type }) => ({ id: messageId++, text, type });

class RelatedProduct extends React.Component {
    constructor(props) {
        super(props);

        addResourceStrings(this.props.wishlistModel.LocalizationItems);
        this.state = this.resetInitialState();
        this.state = { ...this.state, ...this.computeTraitSelections(this.state) };

        this.resetInitialState = this.resetInitialState.bind(this);
        this.onAddToCart = this.onAddToCart.bind(this);
        this.onQuantityUpdate = this.onQuantityUpdate.bind(this);
        this.updateSelectedSku = this.updateSelectedSku.bind(this);
        this.computeTraitSelections = this.computeTraitSelections.bind(this);
        this.findTraitValueIndexes = this.findTraitValueIndexes.bind(this);
        this.addUserMessage = this.addUserMessage.bind(this);
        this.onDismissMessage = this.onDismissMessage.bind(this);
        this.onTraitSelectedFns = this.state.traits.map((trait, i) => this.makeTraitSelectionListener({ dim: i }).bind(this));
        this.updateWishlistLinkClass = this.updateWishlistLinkClass.bind(this);
        this.updateWishlistSrText = this.updateWishlistSrText.bind(this);
        this.updateWishlistLinkText = this.updateWishlistLinkText.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.props.product.Variations.some(variation => variation.SKU === prevState.currentVariation.SKU)) {
            this.setState(this.resetInitialState());
        }
    }

    makeTraitSelectionListener({ dim }) {
        return (index) => {
            this.setState((state, props) => this.computeSelectionStateUpdate(
                dim,
                index,
                state.traitSelections,
                state.traitIndexes,
                state.traits,
                props.product.Variations,
                state.traitMasks));
        };
    }

    resetInitialState() {
        this.initialTraitIndexes = [];
        this.initialTraitMasks = [];

        const currentVariation = this.props.product.Variations[0];

        const traitNames = currentVariation.VariantTraits
            .map(trait => trait.TraitName)
            .filter(traitName =>
                this.props.product.Variations.every(variation =>
                    variation.VariantTraits.some(trait =>
                        trait.TraitName === traitName
                    )
                )
            );

        const traits = traitNames.map(traitName => ({
            Name: traitName,
            Values: []
        }));

        traits.forEach(trait => {
            this.props.product.Variations.forEach(variation => {
                var value = variation.VariantTraits.filter(variantTrait => variantTrait.TraitName === trait.Name)[0].TraitValue;
                if (trait.Values.indexOf(value) === -1) {
                    trait.Values.push(value);
                }
            });

            this.initialTraitIndexes.push(null);
            this.initialTraitMasks.push(trait.Values.map(() => true));
        });

        this.initialTraitSelections = this.initialTraitIndexes;

        return {
            currentVariation,
            filteredVariations: this.props.product.Variations,
            quantity: 1,
            traits,
            traitSelections: this.initialTraitSelections,
            traitIndexes: this.initialTraitIndexes,
            traitMasks: this.initialTraitMasks,
            userMessages: [],
            wishlistLinkClass: 'fa fa-bookmark',
            wishlistSrText: '',
            wishlistLinkText: translateResourceString("/Product/AddToListButton")
        };
    }

    onQuantityUpdate(val) {
        var parsed = Number.parseInt(val, 10);
        if (Number.isNaN(parsed) || parsed < 1) {
            parsed = 0;
        }
        this.setState({ quantity: parsed });
    }

    onAddToCart({ code, quantity, cartName }) {
        const submitMethod = 'POST';
        fetch(
            this.props.addToCartEndpoint,
            makeCatalystFetchPostInit({ code: code, quantity, cartName })
        ).then((response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    if (typeof console !== 'undefined' && console.log) {
                        console.error(`Error sending ${submitMethod} to ${this.props.addToCartEndpoint}: (${response.statusCode}) ${response.statusText}`);
                    }
                    return { hasError: true, message: `An error occurred procesing your request: ${response.statusText}` };
                }
            })
            .then(({ hasError, message }) => {
                if (hasError) {
                    if (!message.endsWith(".")) {
                        message += ".";
                    }

                    this.addUserMessage(createUserMessage({
                        text: `${message} Please reload the page and retry, or contact customer service.`,
                        type: ERROR_USER_ALERT
                    }));
                    return;
                }

                this.addUserMessage(createUserMessage({
                    text: `Item with sku ${code} was added to the cart`,
                    type: INFO_USER_ALERT
                }));

                miniCartClient.updateCart(cartName);
            });
    }

    updateSelectedSku(sku) {
        if (sku === null) {
            return;
        }

        var selectedVariation = this.props.product.Variations.find((variation) => variation.SKU === sku);

        if (typeof selectedVariation === 'undefined') {
            return this.resetInitialState();
        }

        const selections = this.findTraitValueIndexes(selectedVariation);
        return {
            traitSelections: selections,
            traitIndexes: selections,
            currentVariation: selectedVariation
        };
    }

    findTraitValueIndexes(variation) {
        var ret = [];
        if (typeof variation === 'undefined' || typeof this.state.traits === 'undefined') {
            return ret;
        }

        for (let trait of this.state.traits) {
            ret.push(
                trait.Values.indexOf(
                    variation.VariantTraits.find(variantTrait =>
                        variantTrait.TraitName === trait.Name
                    ).TraitValue
                )
            );
        }

        return ret;
    }

    computeSelectionStateUpdate(dim, index, traitSelections, traitIndexes, traits, variations, traitMasks) {
        traitSelections = Array.from(traitSelections);

        if (Number.isNaN(index)) {
            traitSelections = this.initialTraitSelections;
            traitIndexes[dim] = null;
        } else {
            traitSelections[dim] = index;
            traitIndexes[dim] = index;
        }

        const selectedValues = traitSelections.map((selection, i) =>
            (index === null)
                ? null
                : traits[i].Values[selection]);

        // Filter for the variations that match all the currently selected trait values
        let filteredVariations = this.filterVariations(variations, selectedValues, traits);

        //When there are 2 traits, if the new trait selection invalidates the other trait selection, remove the other trait selection
        if (traitSelections.length === 2 && filteredVariations.length === 0) {
            const i = (dim === 0) ? 1 : 0;
            traitSelections[i] = null;
            traitIndexes[i] = null;
            selectedValues[i] = null;

            filteredVariations = this.filterVariations(variations, selectedValues, traits);
        }

        return this.computeTraitSelections({ filteredVariations, traits, traitSelections, traitIndexes, traitMasks });
    }

    computeTraitSelections({ filteredVariations, traits, traitSelections, traitIndexes, traitMasks }) {
        //Figure out what trait values to show based on what the user has already chosen
        if (traitSelections.includes(null)) {
            traitMasks = traits.map((trait, i) => {
                if (traitSelections[i] === null) {
                    // No selection, so get the values for this trait from the remaining variations 
                    const remainingValues = filteredVariations.map(variation =>
                        variation.VariantTraits.find(variantTrait => variantTrait.TraitName === trait.Name).TraitValue
                    );

                    return trait.Values.map(value => remainingValues.includes(value));
                }

                // User already made selection, show all in case they want to change
                return trait.Values.map(() => true);
            });
        }

        if (filteredVariations.length === 1) {
            // One match. Select all the traits for the SKU
            const currentVariation = filteredVariations[0];
            traitSelections = this.findTraitValueIndexes(currentVariation);
            this.updateSelectedSku(currentVariation.SKU);

            return {
                traitMasks,
                traitSelections,
                traitIndexes: traitSelections,
                currentVariation,
                filteredVariations: filteredVariations
            };
        }

        if (filteredVariations.length === 0) {
            // No matches. Clear selections and filters.
            return this.resetInitialState();
        }

        const filteredSkus = filteredVariations.map(variation => variation.SKU);
        if (!filteredSkus.includes(this.state.currentVariation.SKU)) {
            // The currently selected SKU does not meet the criteria and will be filtered out. Select the first available SKU.
            this.updateSelectedSku(filteredVariations[0].SKU);
        }

        return { traitMasks, traitSelections, traitIndexes, filteredVariations };
    }

    filterVariations(allVariations, selectedValues, traits) {
        return allVariations.filter(variation =>
            traits.every((trait, i) =>
                selectedValues[i] === null ||
                variation.VariantTraits.find(variantTrait =>
                    variantTrait.TraitName === trait.Name
                ).TraitValue === selectedValues[i]
            )
        );
    }

    addUserMessage(message) {
        this.setState((prevState) => ({
            userMessages: prevState.userMessages ? prevState.userMessages.concat(message) : [message]
        }));
    }

    onDismissMessage(id) {
        this.setState((prevState) => ({
            userMessages: (prevState.userMessages)
                ? prevState.userMessages.filter((message) => message.Id !== id)
                : []
        }));
    }

    updateWishlistLinkClass(linkClass){
        this.setState({wishlistLinkClass: linkClass});
    }

    updateWishlistSrText(srText){
        this.setState({wishlistSrText: srText});
    }

    updateWishlistLinkText(linkText){
        this.setState({wishlistLinkText: linkText});
    }

    render() {
        const ReactTooltip =  typeof(window) !== 'undefined' && !window.isServerSide && require('react-tooltip').default;
        const {product, wishlistModel} = this.props;
        const {currentVariation: variation, wishlistLinkClass, wishlistSrText, wishlistLinkText } = this.state;
        const currentSku = variation.SKU;

        let hasInventoryDetails = !!variation.InventoryDetails;
        let maxQuantity = hasInventoryDetails ? variation.InventoryDetails.TotalInStock : 0;

        let productUrlWithTraits = product.ProductUrl;
        let traitsAdded = false;

        for (var i = 0; i < this.state.traitSelections.length; i++) {
            if (this.state.traitSelections[i] != null && this.state.traitSelections[i] >= 0) {
                if (!traitsAdded) {
                    productUrlWithTraits += "?";
                    traitsAdded = true;
                } else {
                    productUrlWithTraits += "&";
                }
                productUrlWithTraits += `trait${i + 1}=${this.state.traitSelections[i]}`;
                //productUrlWithTraits += `trait${i + 1}=` + encodeURIComponent(this.state.traits[i].Values[this.state.traitSelections[i]]);
            }
        }

        const layout = {
            showPriceAndInventory: !this.state.traitSelections.includes(null) &&
                typeof variation.PriceViewModel !== 'undefined'
                && variation.PriceViewModel !== null
                && (variation.PriceViewModel.IsSalePrice
                    ? variation.PriceViewModel.SalePrice !== '$0.00'
                    : variation.PriceViewModel.RegularPrice !== '$0.00'),
            showTraitSelectors: product.Variations.length > 0,
            showSkuSelector: this.state.filteredVariations.length > 1 && !this.state.traitIndexes.includes(null),
            showQuoteButton: this.props.siteName === 'Dealer'
                ? variation.ShowQuoteButton
                : variation.ShowQuoteButton && this.props.customerType === 'R',
            showBuyButton: variation.ShowBuyButton && hasInventoryDetails,
            disableCartButton: this.state.traitIndexes.includes(null) || maxQuantity <= 0,
            disableAddToList: this.state.traitIndexes.includes(null)
        };

        let disableCartTip = "";
        if (layout.disableCartButton) {
            if (maxQuantity <= 0) {
                disableCartTip = "Selected product is currently out of stock."
            } else {
                disableCartTip = "Select required fields(*) above to enable";
            }
        }

        if (!layout.ShowBuyButton && layout.showQuoteButton) {
            maxQuantity = 99999;
        }

        return (
            <div className='col-xs-12'>
                <div className='related-product'>
                    <div className='row'>
                        <div className='product-details-left col-xs-6 col-md-3 col-lg-2'>
                            <img src={variation.PrimaryImage.Url} alt={variation.PrimaryImage.AltText} className='related-product-image' />
                        </div>
                        <div className='product-details-center col-xs-6 col-md-3 col-lg-6'>
                            <div className='row'>
                                <div className='col-xs-12'>
                                    <div className='variation-details' id='variation-details'>
                                        <a href={productUrlWithTraits} className='related-product-link'>{product.Name}</a>
                                        <div dangerouslySetInnerHTML={{ __html: product.Description }} />
                                    </div>
                                </div>
                                {layout.showPriceAndInventory && <ProductPriceAndInventory currentVariation={variation} />}
                            </div>
                        </div>
                        <div className='product-details-right col-xs-12 col-sm-6 col-sm-offset-6 col-md-5 col-md-offset-1 col-lg-4 col-lg-offset-0'>
                            <div className='addtocart-wrapper text-left'>
                                <form role="form" className='form-horizontal'>

                                    {layout.showTraitSelectors && this.state.traits.map((trait, traitIndex) => (
                                        typeof trait !== 'undefined' && trait.Values.length > 0 && traitIndex < 2 &&
                                        <TraitSelector
                                            onSelect={this.onTraitSelectedFns[traitIndex]}
                                            label={trait.Name}
                                            values={trait.Values}
                                            currentValue={this.state.traitIndexes[traitIndex]}
                                            n={traitIndex}
                                            valueMask={this.state.traitMasks[traitIndex]}
                                            key={`trait${traitIndex}`}
                                        />
                                    ))}

                                    {layout.showSkuSelector && (
                                        <div className='form-group'>
                                            <div className='col-xs-12'>
                                                <label className='' htmlFor='skuvalue'> Item</label>
                                                <select
                                                    id="skuvalue"
                                                    className='form-control'
                                                    value={currentSku}
                                                    onChange={(e) => this.setState(this.updateSelectedSku(e.target.value))}
                                                >
                                                    {this.state.filteredVariations.map(v => (<option key={v.SKU} value={v.SKU}>{v.SKU}</option>))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    <div className='clearfix' />

                                    {(layout.showBuyButton || layout.showQuoteButton) && (
                                        <div className='form-group'>
                                            <div className='col-xs-6'>
                                                <QuantityInput onQuantityUpdate={this.onQuantityUpdate} currentQuantity={this.state.quantity} maxQuantity={maxQuantity} />
                                            </div>
                                            <div className='col-xs-6'>
                                                <span data-tip={disableCartTip} data-tip-disable={!layout.disableCartButton}>
                                                    <button
                                                        type="button"
                                                        className='add-to-quote-button btn btn-primary mb-2'
                                                        disabled={layout.disableCartButton}
                                                        style={{ verticalAlign: 'middle', width: '100%' }}
                                                        onClick={(e) => this.onAddToCart({
                                                            code: currentSku,
                                                            quantity: this.state.quantity,
                                                            cartName: layout.showBuyButton ? "Default" : "Quote Request"
                                                        })}
                                                    >
                                                        {`Add to ${layout.showBuyButton ? 'Cart' : 'Quote'}`}
                                                    </button>
                                                </span>
                                                {wishlistModel.ShowWishlist && (
                                                <span data-tip="Select required fields(*) above to enable" data-tip-disable={!layout.disableCartButton}>
                                                    <AddToWishlistApp
                                                        Name={variation.Name}
                                                        SKU={currentSku}
                                                        ImageUrl={wishlistModel.ImageUrl}
                                                        Wishlists={wishlistModel.Wishlists}
                                                        AddItemEndpoint={wishlistModel.AddItemEndpoint}
                                                        AddListEndpoint={wishlistModel.AddListEndpoint}
                                                        UserIsAuthorized={wishlistModel.UserIsAuthorized}
                                                        LoginLink={wishlistModel.LoginLink}
                                                        LocalizationItems={wishlistModel.LocalizationItems}
                                                        Disabled={layout.disableAddToList}
                                                        linkClass={wishlistLinkClass}
                                                        setLinkClass={this.updateWishlistLinkClass}
                                                        srText={wishlistSrText}
                                                        setSrText={this.updateWishlistSrText}
                                                        linkText={wishlistLinkText}
                                                        setLinkText={this.updateWishlistLinkText}
                                                        UpdateWishlists={this.props.updateWishlists}
                                                    >
                                                    </AddToWishlistApp>
                                                </span>)}
                                                {ReactTooltip && <ReactTooltip />}
                                            </div>
                                        </div>
                                    )}
                                </form>
                            </div>
                            <MessagesList messages={this.state.userMessages} onDismiss={this.onDismissMessage} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

RelatedProduct.propTypes = {
    addToCartEndpoint: PropTypes.string,
    onSelect: PropTypes.func,
    product: PropTypes.object,
    wishlistModel: PropTypes.object
};

export default RelatedProduct;